import { Col, Row } from "react-bootstrap";
import { UseFormRegisterReturn } from "react-hook-form";
import styles from "./TextInput.module.scss";
import { useState, KeyboardEvent, ChangeEvent } from "react";

export enum EInputMode {
  primary,
  light,
}

interface IProps {
  className?: string;
  disabled?: boolean;
  fontSize?: number;
  placeholder?: string;
  name?: string;
  type?: "text" | "password" | "email" | "tel" | "number";
  label?: string;
  required?: boolean;
  useFormRegister?: UseFormRegisterReturn;
  textAlign?: "left" | "center";
  mode?: EInputMode;
  onBlur?: (e: any) => void;
  min?: any;
  value?: any;
  maxLength?: number;
  onChange?: (value: any) => void;
  errors?: { isShow: boolean; message: string }[];
  fontFamily?:
    | "Poppins"
    | "Arial"
    | "Roboto"
    | "sans-serif"
    | "Helvetica"
    | "Open Sans"
    | "Montserrat"
    | "Lato"
    | "Georgia"
    | "Times New Roman"
    | "Courier New"
    | "Verdana"
    | "Tahoma"
    | "Noto Sans"
    | "Oswald"
    | "Merriweather";
  fontStyle?: "normal" | "italic" | "oblique";
  fontWeight?: "normal" | "bold" | "lighter" | "bolder" | number;
  color?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
}

const TextInput = (props: IProps) => {
  const {
    type = "text",
    textAlign = "left",
    mode = EInputMode.light,
    placeholder = "",
    fontSize = 16,
    inputMode = "text",
  } = props;

  const [showPassword, setShowPassword] = useState(false);

  const getColor = (color: string | undefined) => {
    switch (color) {
      case "success":
        return "#2FD664";
      case "danger":
        return "#D85454";
      case "light":
        return "#ffffff";
      case "primary":
        return "#2c62ae";
      case "gray":
        return "#061D47";
      case "blue":
        return "#1E4894";
      default:
        return "#ffffff";
    }
  };

  const getLabelClass = (mode: EInputMode) => {
    switch (mode) {
      case EInputMode.primary:
        return styles.labelPrimary;
      case EInputMode.light:
        return styles.labelLight;
      default:
        return styles.labelPrimary;
    }
  };

  const getInputClass = (mode: EInputMode) => {
    switch (mode) {
      case EInputMode.primary:
        return styles.inputPrimary;
      case EInputMode.light:
        return styles.inputLight;
      default:
        return styles.inputPrimary;
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // For numeric inputs, prevent non-numeric characters
    if (
      props.type === "tel" ||
      props.type === "number" ||
      props.inputMode === "numeric"
    ) {
      if (
        !/[0-9]/.test(e.key) && // Allow digits
        e.key !== "Backspace" && // Allow backspace
        e.key !== "Tab" && // Allow tab navigation
        e.key !== "Delete" && // Allow delete
        !e.key.startsWith("Arrow") // Allow arrow keys
      ) {
        e.preventDefault();
      }
    }

    // Call custom onKeyDown if provided
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // For numeric inputs, strip non-numeric characters
    if (
      props.type === "tel" ||
      props.type === "number" ||
      props.inputMode === "numeric"
    ) {
      e.target.value = e.target.value.replace(/[^0-9]/g, "");
    }

    // Call custom onChange if provided
    if (props.onChange) {
      props.onChange(e.target.value);
    }
  };

  return (
    <Row className={[props.className].join(" ")}>
      <Col>
        {props.label && (
          <label className={[styles.label, getLabelClass(mode)].join(" ")}>
            {props.label}{" "}
            {props.required && <span className="text-danger">*</span>}
          </label>
        )}
        <div className={styles.inputContainer}>
          {type === "password" && (
            <button
              type="button"
              className={styles.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "👁️" : "👁️‍🗨️"}
            </button>
          )}
          <input
            className={["w-100", styles.input, getInputClass(mode)].join(" ")}
            style={{
              textAlign: textAlign,
              fontFamily: "poppins",
              fontStyle: props.fontStyle,
              fontWeight: props.fontWeight,
              color: getColor(props?.color),
              fontSize,
              border: "1px solid #E5F0FE",
            }}
            disabled={!!props.disabled}
            placeholder={placeholder}
            name={props.name && props.name}
            type={type === "password" && showPassword ? "text" : type}
            inputMode={inputMode}
            onBlur={props.onBlur}
            min={props.min}
            maxLength={props.maxLength}
            value={props.value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            {...props.useFormRegister}
          />
          {type === "password" && (
            <button
              type="button"
              className={styles.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "👁️" : "👁️‍🗨️"}
            </button>
          )}
        </div>
        {props.errors?.map(
          (error, index) =>
            error.isShow && (
              <p key={index} className={[styles.error].join(" ")}>
                {error.message}
              </p>
            )
        )}
      </Col>
    </Row>
  );
};

export default TextInput;
